import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sun, Moon, Menu, X, ChevronDown } from 'lucide-react';
import { shouldHideSubscriptionPurchase } from '../../utils/platform';

const aboutNavItem = { label: 'About', path: '/about' };
const contactNavItem = { label: 'Contact Us', path: '/contact-us' };
const paymentNavItem = { label: 'Payment', path: '/payment', hasDropdown: true };
const legalNavItem = { label: 'Legal', path: '#', hasDropdown: true };

const paymentSubItems = [
  { label: 'Terms & Conditions', path: '/terms-and-conditions' },
  { label: 'Privacy Policy', path: '/privacy-policy' },
  { label: 'Account Deletion', path: '/account-deletion' },
  { label: 'Return & Refund Policy', path: '/return-refund' },
];

const MainNavbar = ({ theme = 'light', toggleTheme = () => {}, activePage }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const [mobilePaymentOpen, setMobilePaymentOpen] = useState(false);
  const hidePaymentNav = shouldHideSubscriptionPurchase();

  const navItems = useMemo(
    () => [
      aboutNavItem,
      hidePaymentNav ? legalNavItem : paymentNavItem,
      contactNavItem,
    ],
    [hidePaymentNav]
  );

  const handleNavigation = (item) => {
    if (item.path === '#') {
      return;
    }
    navigate(item.path);
    setMobileMenuOpen(false);
  };

  const handleSubItemNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setMobilePaymentOpen(false);
  };

  return (
    <nav
      className={`fixed w-full z-40 top-0 start-0 transition-all duration-500 backdrop-blur-md ${
        theme === 'dark'
          ? 'bg-gradient-to-r from-[#483D8B]/80 to-[#2d1b4e]/80 border-b border-white/10'
          : 'bg-gradient-to-br from-blue-50/70 via-indigo-50/70 to-purple-50/70 border-b border-gray-200/50'
      }`}
    >
      <div className="w-full flex items-center gap-4 px-4 sm:px-6 py-3">
        <button
          onClick={() => navigate('/home')}
          className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity duration-300"
        >
          <img
            src="/logo1.png"
            alt="TrackYourWheelz Logo"
            className="h-8 w-8 sm:h-10 sm:w-10 object-contain rounded-md"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <span
            className={`font-semibold text-lg sm:text-xl transition-colors duration-500 ${
              theme === 'dark' ? 'text-white' : 'text-gray-800'
            }`}
          >
            TrackYourWheelz
          </span>
        </button>

        <div className="hidden md:flex flex-1 items-center justify-center gap-4 lg:gap-6">
          {navItems.map((item) => {
            const isActive = item.label === activePage;
            
            if (item.hasDropdown) {
              return (
                <div
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() => setPaymentDropdownOpen(true)}
                  onMouseLeave={() => setPaymentDropdownOpen(false)}
                >
                  <button
                    onClick={() => handleNavigation(item)}
                    className={`flex items-center gap-1 text-sm md:text-base font-medium transition-all duration-300 no-underline ${
                      isActive
                        ? theme === 'dark'
                          ? 'text-white'
                          : 'text-blue-700'
                        : theme === 'dark'
                          ? 'text-white hover:text-white/90'
                          : 'text-gray-700 hover:text-blue-700'
                    }`}
                  >
                    {item.label}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${paymentDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <span
                    className={`absolute left-0 -bottom-1 h-[2px] transition-all duration-300 ${
                      isActive ? 'w-full' : 'w-0 group-hover:w-full'
                    } ${theme === 'dark' ? 'bg-blue-400' : 'bg-blue-600'}`}
                  />
                  
                  {/* Dropdown Menu */}
                  <div
                    className={`absolute top-full left-0 mt-2 w-56 rounded-lg shadow-xl backdrop-blur-xl border transition-all duration-300 ${
                      paymentDropdownOpen
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2'
                    } ${
                      theme === 'dark'
                        ? 'bg-[#2d1b4e]/95 border-white/10'
                        : 'bg-white/95 border-gray-200/50'
                    }`}
                  >
                    <div className="py-2">
                      {paymentSubItems.map((subItem) => (
                        <button
                          key={subItem.label}
                          onClick={() => handleSubItemNavigation(subItem.path)}
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-all duration-300 ${
                            theme === 'dark'
                              ? 'text-gray-300 hover:bg-white/10 hover:text-blue-300'
                              : 'text-gray-700 hover:bg-gray-100 hover:text-blue-700'
                          }`}
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            
            return (
              <button
                key={item.label}
                onClick={() => handleNavigation(item)}
                className={`group relative text-sm md:text-base font-medium transition-all duration-300 no-underline ${
                  isActive
                    ? theme === 'dark'
                      ? 'text-white'
                      : 'text-blue-700'
                    : theme === 'dark'
                      ? 'text-white hover:text-white/90'
                      : 'text-gray-700 hover:text-blue-700'
                }`}
              >
                {item.label}
                <span
                  className={`absolute left-0 -bottom-1 h-[2px] transition-all duration-300 ${
                    isActive ? 'w-full' : 'w-0 group-hover:w-full'
                  } ${theme === 'dark' ? 'bg-blue-400' : 'bg-blue-600'}`}
                />
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-3 md:gap-4">
          <Button
            onClick={() => navigate('/login')}
            className={`hidden md:flex h-9 px-4 text-sm font-semibold text-white shadow-md transition-all duration-300 ${
              theme === 'dark'
                ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
            }`}
          >
            Login / Sign Up
          </Button>

          <Button
            onClick={toggleTheme}
            variant="ghost"
            size="sm"
            className={`p-2 rounded-lg transition-all duration-500 ${
              theme === 'dark'
                ? 'text-white hover:bg-white/10'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          <button
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className={`md:hidden p-2 rounded-lg transition-all duration-300 ${
              theme === 'dark'
                ? 'text-gray-200 hover:bg-white/10'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div
        className={`md:hidden overflow-hidden transition-all duration-700 ease-in-out transform ${
          mobileMenuOpen ? 'opacity-100 translate-y-0 visible max-h-96' : 'opacity-0 -translate-y-5 invisible max-h-0'
        }`}
      >
        <div
          className={`mx-4 mt-2 mb-4 p-4 rounded-2xl shadow-2xl backdrop-blur-xl border transition-all duration-500 ${
            theme === 'dark'
              ? 'bg-[#2d1b4e]/90 border-white/10 text-white'
              : 'bg-white/80 border-gray-200/50 text-gray-800'
          }`}
        >
          <div className="flex flex-col space-y-4 text-center">
            {navItems.map((item) => {
              const isActive = item.label === activePage;
              
              if (item.hasDropdown) {
                return (
                  <div key={item.label} className="space-y-2">
                    <button
                      onClick={() => {
                        setMobilePaymentOpen(!mobilePaymentOpen);
                      }}
                      className={`w-full flex items-center justify-center gap-2 text-base font-semibold py-2 rounded-lg transition-all duration-300 ${
                        isActive
                          ? theme === 'dark'
                            ? 'bg-white/10 text-white'
                            : 'bg-gray-100 text-blue-600'
                          : theme === 'dark'
                            ? 'text-white hover:bg-white/10 hover:text-white/90'
                            : 'hover:bg-gray-100 hover:text-blue-600'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${mobilePaymentOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Mobile Sub-items */}
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        mobilePaymentOpen ? 'max-h-56 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="flex flex-col space-y-2 pl-4">
                        {paymentSubItems.map((subItem) => (
                          <button
                            key={subItem.label}
                            onClick={() => handleSubItemNavigation(subItem.path)}
                            className={`text-sm font-medium py-2 rounded-lg transition-all duration-300 ${
                              theme === 'dark'
                                ? 'text-white hover:bg-white/10 hover:text-white/90'
                                : 'hover:bg-gray-100 hover:text-blue-600 text-gray-700'
                            }`}
                          >
                            {subItem.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              
              return (
                <button
                  key={item.label}
                  onClick={() => handleNavigation(item)}
                  className={`text-base font-semibold py-2 rounded-lg transition-all duration-300 ${
                    isActive
                      ? theme === 'dark'
                        ? 'bg-white/10 text-white'
                        : 'bg-gray-100 text-blue-600'
                      : theme === 'dark'
                        ? 'text-white hover:bg-white/10 hover:text-white/90'
                        : 'hover:bg-gray-100 hover:text-blue-600'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}

            <div className="pt-3 border-t border-gray-200/20">
              <Button
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                className={`w-full h-9 text-white shadow-md transition-all duration-300 ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e]'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600'
                }`}
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default MainNavbar;

